import React from 'react';
import { motion } from 'framer-motion';
import { Zap, FileText, Clock, Award, ShieldCheck, Lock, CreditCard, ArrowRight, Folder, Paperclip } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-4 pb-6 overflow-hidden" data-id="element-400">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-radial-glow pointer-events-none opacity-60" data-id="element-401"></div>
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-energy/5 blur-[120px] rounded-full pointer-events-none" data-id="element-402"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-403">
        {/* Folder Tab */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex justify-center mb-2" data-id="element-404">
          <div className="bg-petroleum border border-electric/40 rounded-t-lg px-3 py-1 flex items-center gap-1.5 transform -rotate-1" data-id="element-405">
            <Folder className="w-3 h-3 text-electric" data-id="element-406" />
            <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-electric" data-id="element-407">
              Expediente 01 — Contratación
            </span>
          </div>
        </motion.div>

        {/* Announcement Bar */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center justify-center mb-4" data-id="element-408">
          <div className="bg-petroleum/80 backdrop-blur-sm border border-electric/30 rounded-2xl py-3 px-8 md:px-16 flex flex-col items-center gap-1.5 text-xs md:text-sm w-full max-w-2xl" data-id="element-409">
            <span className="inline-flex items-center gap-1.5 bg-electric/15 text-electric font-semibold rounded-full px-2.5 py-0.5" data-id="element-410">
              <Zap className="w-3.5 h-3.5 fill-electric" data-id="element-411" />
              BIBLIOTECAS DIGITALES™
            </span>
            <span className="text-textsecondary" data-id="element-412">
              Cómo Conseguir Entrevistas
            </span>
          </div>
        </motion.div>

        <div className="max-w-5xl mx-auto text-center" data-id="element-413">
          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="md:text-5xl lg:text-6xl font-extrabold mb-4 text-[33px] leading-tight" data-id="element-414">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-gold" data-id="element-415">
              Cómo Conseguir Entrevistas
            </span>
            <br data-id="element-416" />
            <span className="text-white" data-id="element-417">Aunque Tu CV Hoy No Funcione</span>
          </motion.h1>

          {/* Mockup + Floating CTA card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="relative mb-4 pb-16 md:pb-20" data-id="element-418">
            <div className="absolute inset-0 bg-electric/20 blur-[100px] rounded-full" data-id="element-419"></div>

            {/* Ruled paper texture behind mockup */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none rounded-3xl" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, #ffffff 0px, #ffffff 1px, transparent 1px, transparent 32px)'
          }} data-id="element-420"></div>

            {/* Paperclip */}
            <div className="absolute top-0 left-1/2 -translate-x-32 md:-translate-x-48 z-20 transform -rotate-45 text-textsecondary/50" data-id="element-421">
              <Paperclip className="w-8 h-8 md:w-10 md:h-10" data-id="element-422" />
            </div>

            <img src="https://cdn.magicpatterns.com/uploads/997QVtqp9JYXGiqGNXcidQ/mockup_shopi_sf.png" alt="SuperPack Entrevistas — Cómo Conseguir Entrevistas" className="relative z-10 w-full max-w-[320px] md:max-w-md mx-auto max-h-[46vh] object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-423" />

            {/* Floating CTA pill */}
            <a href="#pricing" className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20 w-[92%] max-w-md bg-electric hover:bg-electric/90 text-white rounded-full pl-6 pr-2 py-2 shadow-2xl shadow-electric/50 glow-electric flex items-center justify-between gap-3 transition-all duration-300 transform hover:-translate-y-0.5 group" data-id="element-424">
              <div className="flex flex-col items-start leading-tight" data-id="element-425">
                <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-white/80" data-id="element-426">
                  Acceso Instantáneo
                </span>
                <span className="text-sm md:text-base font-extrabold uppercase tracking-wide" data-id="element-427">
                  Quiero el SuperPack ahora
                </span>
              </div>
              <span className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white text-electric flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-428">
                <ArrowRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} data-id="element-429" />
              </span>
            </a>
          </motion.div>

          {/* Subhead */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-lg md:text-xl text-textsecondary mb-5 max-w-2xl mx-auto" data-id="element-430">
            El sistema N°1 para personas que buscan trabajo y quieren conseguir
            entrevistas reales en Argentina usando el método Anti-ATS.
          </motion.p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6 text-sm md:text-base font-medium" data-id="element-431">
            <div className="flex items-center gap-2" data-id="element-432">
              <FileText className="w-5 h-5 text-electric" data-id="element-433" />
              <span className="text-[10px]" data-id="element-434">100% PDF</span>
            </div>
            <div className="hidden md:block w-1 h-1 rounded-full bg-textsecondary/50" data-id="element-435"></div>
            <div className="flex items-center gap-2" data-id="element-436">
              <Clock className="w-5 h-5 text-electric" data-id="element-437" />
              <span className="text-[10px]" data-id="element-438">Acceso en 5 min</span>
            </div>
            <div className="hidden md:block w-1 h-1 rounded-full bg-textsecondary/50" data-id="element-439"></div>
            <div className="flex items-center gap-2" data-id="element-440">
              <Award className="w-5 h-5 text-electric" data-id="element-441" />
              <span className="text-[10px]" data-id="element-442">
                De CV Invisible a Entrevistas Reales
              </span>
            </div>
          </div>

          {/* Trust micro-badges */}
          <div className="flex flex-col items-center gap-4" data-id="element-443">
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs md:text-sm text-textsecondary" data-id="element-444">
              <div className="flex items-center gap-1" data-id="element-445">
                <ShieldCheck className="w-4 h-4" data-id="element-446" /> Garantía 7 días
              </div>
              <div className="flex items-center gap-1" data-id="element-447">
                <Lock className="w-4 h-4" data-id="element-448" /> Pago seguro
              </div>
              <div className="flex items-center gap-1" data-id="element-449">
                <CreditCard className="w-4 h-4" data-id="element-450" /> Mercado Pago
              </div>
            </div>

            {/* Dashed file edge divider */}
            <div className="w-full max-w-md border-b border-dashed border-electric/30 mt-2" data-id="element-451"></div>
          </div>
        </div>
      </div>
    </section>;
}